/**
 * Timbrica Room Connect — shared P2P room-protocol layer over PeerJS.
 *
 * Sits on top of __WebRTC.createPeer (ICE / STUN / TURN config) and
 * standardises the *room* layer that was open-coded across 10+ tools.
 *
 * v1.0 (checkers): 1v1 data-only rooms — `hostRoom`, `joinRoom`.
 * v1.1 (this file): extends API to cover every shape audited:
 *   - 1v1 data           : checkers, tic-tac-toe (already correct), volleyball-game
 *   - 1v1 data + media   : volleyball-game, light-detector (remote viewer)
 *   - host-N viewers     : baby-monitor, live-audio, video-surveillance
 *   - rotating-host      : walkie-talkie (partial — uses `attemptHostClaim` helper)
 *
 * Multi-party mesh (audio-call, video-call) lives in __RoomConnect.Mesh
 * (separate sub-module) — different topology (every peer dials every peer
 * via host-introduction) doesn't fit the host/guest dichotomy here.
 *
 * The module never touches the DOM. Callers decide their own status text /
 * toast / telemetry. This keeps copy in tool-specific blades and keeps the
 * abstraction thin.
 */
(function(w){
    'use strict';

    var DEFAULT_TIMEOUT = 15000;
    var DEFAULT_CODE_LEN = 6;
    var CODE_ALPHABET = 'bcdfghjkmnpqrstvwxyz23456789'; // no vowels (no rude words), no 0/1 (no confusion)
    var CODE_REGEX = /^[a-z0-9]{6,8}$/;
    var ROOM_FULL_PAYLOAD = [6]; // [op, ...] convention shared by checkers / tic-tac-toe / volleyball-game
    // How long the host will wait for a refusal to leave the send buffer before
    // closing the channel anyway. See _closeWhenDrained.
    var REJECT_DRAIN_MAX_MS = 1500;
    var REJECT_DRAIN_TICK_MS = 50;

    /**
     * Per-room guest session token — survives F5 reload so the host can
     * distinguish "same guest reconnecting after refresh" from "third
     * player trying to crash the party". Without this, reloading a tab on
     * /checkers/{room} fired 30+ false ROOM_FULL toasts in 30 days
     * (the host still had the dead connection in its set, count >= 1,
     * second join rejected). Token is per-room (rc:gst:{prefix}{code} in
     * sessionStorage so a new browser tab gets a different token and is
     * correctly rejected). 24h TTL.
     */
    function _sessionToken(prefix, code){
        if (typeof sessionStorage === 'undefined' || !code) return null;
        var key = 'rc:gst:' + (prefix || '') + code;
        try {
            var existing = sessionStorage.getItem(key);
            if (existing) return existing;
            // 16-hex-char random token. Math.random is fine for distinguishing
            // a returning peer from a third party — not a security boundary.
            var tok = '';
            for (var i = 0; i < 16; i++) tok += ((Math.random() * 16) | 0).toString(16);
            sessionStorage.setItem(key, tok);
            return tok;
        } catch(_){ return null; }
    }

    function _safeDestroy(peer){
        if (peer){ try { peer.destroy(); } catch(_){} }
    }

    /**
     * Is this frame the host saying «no seat left»?
     *
     * Matches the shared [6] convention only. `hostRoom` lets a caller swap in
     * its own `roomFullPayload`, and the guest has no way to learn about that —
     * so a tool that overrides it owns its own detection, as it did before.
     */
    function _isRoomFull(d){
        return Array.isArray(d) && d.length > 0 && d[0] === ROOM_FULL_PAYLOAD[0];
    }

    /* ⚠️ Те же брокеры, что в public/js/webrtc-config.js (PEER_SERVERS).
       Голый `new Peer()` НЕ нейтрален: замер настоящим WebSocket на
       /ru/screen-share — он открывает
         wss://0.peerjs.com/peerjs?key=peerjs
       то есть ПУБЛИЧНЫЙ сторонний брокер, тогда как наш путь идёт в
         wss://timbrica.com/peer/peerjs?key=timbrica
       Последствий два, и оба тихие. Во-первых, две стороны комнаты
       оказываются на РАЗНЫХ брокерах, и гость гарантированно получает
       `peer-unavailable` — комната «не найдена», хотя хост в эфире.
       Во-вторых, туда уходит сигналинг: код комнаты, peer-id и WebRTC-оффер
       с кандидатами (то есть адреса пользователя).
       Путь достижим при гонке загрузки: все 20 страниц подключают
       webrtc-config.js, но обрыв загрузки скрипта на этом сайте — обычное
       дело (за 9 дней 241 отказ у tool-rating.js, 111 у timbrica-core.js).
       Держится в согласии со своим источником тестом
       RoomBrokerFallbackMatchesConfigTest. */
    var _FALLBACK_PEER_SERVERS = {
        ru:   { host: 'timbrica.com', port: 443, path: '/peer', secure: true, key: 'timbrica' },
        intl: { host: 'rtc.tembrica.com', port: 443, path: '/peer', secure: true, key: 'timbrica' }
    };
    /* Регион страницы — тот же выбор, что в webrtc-config.js: сервер кладёт
       window.__RTC_REGION в <head> (по локали пути), без него решает хост.
       Брокер другого региона = другая комната: хост и гость не встретятся. */
    function _fallbackPeerServer(){
        var r = w.__RTC_REGION;
        if (!_FALLBACK_PEER_SERVERS[r]) {
            r = /(^|\.)tembrica\.com$/i.test(String((w.location && w.location.hostname) || '')) ? 'intl' : 'ru';
        }
        return _FALLBACK_PEER_SERVERS[r];
    }
    function _newPeer(id){
        // PeerJS is required for both paths — the __WebRTC.createPeer helper
        // calls `new Peer()` internally, and the direct fallback obviously
        // needs it too. If the peerjs.min.js script tag failed to load
        // (Sentry TIMBRICA-FRONTEND-5B: onerror= fallback can race with the
        // next <script> tag in the page), surface a typed error so callers
        // can dispatch to onError({type:'peerjs-missing'}) instead of dying
        // with `ReferenceError: Peer is not defined`.
        if (typeof w.Peer === 'undefined') {
            var e = new Error('PeerJS library failed to load');
            e.type = 'peerjs-missing';
            throw e;
        }
        if (w.__WebRTC && typeof w.__WebRTC.createPeer === 'function'){
            return w.__WebRTC.createPeer(id);
        }
        // Запасной путь для страниц, подключивших peerjs напрямую, ИЛИ когда
        // webrtc-config.js не доехал. Брокер обязан остаться НАШИМ.
        return id ? new w.Peer(id, _fallbackPeerServer()) : new w.Peer(_fallbackPeerServer());
    }

    /**
     * Room code helpers — shared across every multiplayer tool.
     * Inlined in 9 blades pre-v1.1; centralise to keep alphabet/length consistent.
     */
    function generateCode(len){
        len = len || DEFAULT_CODE_LEN;
        var code = '';
        for (var i = 0; i < len; i++){
            code += CODE_ALPHABET[(Math.random() * CODE_ALPHABET.length) | 0];
        }
        return code;
    }

    function validateCode(raw){
        if (typeof raw !== 'string') return false;
        var cleaned = raw.toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8);
        return CODE_REGEX.test(cleaned) ? cleaned : false;
    }

    /**
     * Same shape as validateCode but returns a result object so tools can
     * render a localised inline error instead of a silent no-op. v1.2 tools
     * had to repeat the `if (!code) {...}` ternary; v1.3 centralises the
     * empty-vs-format distinction.
     *   { code: 'abc123', error: null }
     *   { code: null, error: 'empty' | 'format' }
     */
    function validateCodeDetailed(raw){
        if (typeof raw !== 'string' || raw.trim() === ''){
            return { code: null, error: 'empty' };
        }
        var code = validateCode(raw);
        return code ? { code: code, error: null } : { code: null, error: 'format' };
    }

    /**
     * Host-stamp helpers — let a tool detect on next page-load whether
     * THIS browser was the host of THIS room, so reload-on-`/room-url`
     * doesn't silently fall into a viewer-timeout against the now-dead
     * peer-id. Tool decides what to do with the answer (resume vs
     * fresh join vs prompt).
     *
     * Key shape: `rc:host:{prefix}{code}` → JSON `{t: epoch_ms}`.
     * TTL is hard-coded to 10 min — short enough not to leak room codes
     * on a shared device, long enough to cover an accidental reload /
     * back-and-forward navigation.
     *
     * Tool MUST call clearHostedRoom() in its stop/destroy path so a
     * deliberate "end session" doesn't trigger the resume prompt next
     * time the user visits.
     */
    var HOST_STAMP_TTL_MS = 10 * 60 * 1000;
    function _hostKey(prefix, code){ return 'rc:host:' + (prefix || '') + code; }

    function markHostedRoom(prefix, code){
        if (!code) return;
        try { localStorage.setItem(_hostKey(prefix, code), JSON.stringify({ t: Date.now() })); } catch(_){}
    }

    function clearHostedRoom(prefix, code){
        if (!code) return;
        try { localStorage.removeItem(_hostKey(prefix, code)); } catch(_){}
    }

    function isMyHostedRoom(prefix, code){
        if (!code) return false;
        try {
            var raw = localStorage.getItem(_hostKey(prefix, code));
            if (!raw) return false;
            var data = JSON.parse(raw);
            if (!data || typeof data.t !== 'number') return false;
            if (Date.now() - data.t > HOST_STAMP_TTL_MS){
                // Expired — clean up so we don't keep returning false
                // on every visit until the TTL slot is reused.
                localStorage.removeItem(_hostKey(prefix, code));
                return false;
            }
            return true;
        } catch(_){
            return false;
        }
    }

    /**
     * Telemetry helper. Standardises event names under `room_*` namespace
     * so per-tool dashboards / SQL queries stay consistent.
     *
     *   __RoomConnect.emit('host_open',   { tool, code_len, role: 'host' });
     *   __RoomConnect.emit('peer_error',  { tool, side: 'guest', msg });
     *   __RoomConnect.emit('joined',      { tool, via: 'code'|'url' });
     *
     * Routes through `__aeCritical` (anonymous Redis counter, GDPR-friendly)
     * with `__ae` fallback for tools that depend on the analytics_events row.
     */
    function emit(event, data){
        var name = 'room_' + event;
        try {
            if (w.__aeCritical) return w.__aeCritical(name, data || {});
            if (w.__ae) return w.__ae(name, data || {});
        } catch(_){}
    }

    /**
     * Browser family classifier — coarse-grained label for failure
     * aggregation. We DON'T want full UA strings in Redis (PII risk +
     * cardinality explosion), just enough to spot patterns like "60% of
     * walkie-talkie join failures on HuaweiBrowser".
     *
     * Order matters: HuaweiBrowser advertises itself as Chrome too, so
     * the Huawei/Samsung/MIUI/in-app checks MUST precede the generic
     * Chrome/Firefox/Safari match.
     */
    function browserFamily(){
        var ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
        if (/HuaweiBrowser/i.test(ua)) return 'huawei';
        if (/SamsungBrowser/i.test(ua)) return 'samsung';
        if (/MiuiBrowser|XiaoMi/i.test(ua)) return 'miui';
        if (/YaBrowser/i.test(ua)) return 'yandex';
        if (/UCBrowser/i.test(ua)) return 'uc';
        if (/Instagram|FBAN|FBAV|FB_IAB|Line\//i.test(ua)) return 'inapp';
        if (/Edg\//i.test(ua)) return 'edge';
        if (/Firefox\//i.test(ua)) return 'firefox';
        if (/Chrome\//i.test(ua)) return 'chrome';
        if (/Safari\//i.test(ua)) return 'safari';
        return 'other';
    }

    /**
     * Structured failure emitter — feeds the `rj:{slug}:{reason}:{browser}:{hour}`
     * Redis counters consumed by `php artisan rooms:join-failures`.
     *
     * Reasons are a closed enum so the dashboard groups stay readable:
     *   broker_timeout      — PeerJS broker didn't open within timeoutMs
     *   p2p_timeout         — broker OPENED, but the P2P data channel to the
     *                         host didn't within timeoutMs (ICE dead-end —
     *                         STUN-only pair behind symmetric NAT/VPN; the
     *                         relay-needed signal)
     *   broker_unreachable  — peer.on('error', {type:'network'|'socket-error'})
     *   peer_unavailable    — host peer-id is not online (room dead)
     *   ice_failed          — RTCPeerConnection ICE failed/disconnected
     *   data_conn_error     — DataConnection.on('error') after open
     *   media_call_failed   — call.answer/on('error')
     *   internal            — fallthrough for anything we couldn't classify
     */
    function emitJoinFailed(tool, reason){
        // Tool slug fallback: read from <meta name="tool-slug"> (set by every
        // tool blade via the layout). Lets us measure without forcing every
        // caller to pass `tool:` explicitly during the roll-out.
        if (!tool && typeof document !== 'undefined'){
            var meta = document.querySelector('meta[name="tool-slug"]');
            if (meta) tool = meta.content;
        }
        if (!tool || !reason) return;
        try {
            if (w.__aeCritical){
                w.__aeCritical('room_join_failed', {
                    slug: tool,
                    reason: reason,
                    browser: browserFamily()
                });
            }
        } catch(_){}
    }

    /**
     * Auto-reconnect helper. Two trigger surfaces collapsed into one opt:
     *
     *  1. visibilitychange — when a tab unblurs after browser/OS suspend,
     *     PeerJS signaling often went dead silently. Reconnect on focus.
     *  2. peer.on('disconnected') — same outcome but PeerJS itself notices.
     *     Without this listener, the peer is dead-but-thinks-alive and the
     *     next message vanishes. Pre-v1.2 every tool wired this inline.
     *
     * Both PeerJS exposes `.disconnected` (signaling lost, peer object
     * reusable) distinct from `.destroyed` (terminal). Reconnect only the
     * former.
     *
     * Returns a cleanup function the caller invokes on destroy.
     * Caller is also responsible for invoking `_wireDisconnectedRecovery(peer)`
     * each time a fresh peer is installed (e.g. after regenCode collision).
     */
    function _wireDisconnectedRecovery(peer){
        if (!peer) return;
        peer.on('disconnected', function(){
            // Race: if peer.destroy() landed before this fires, skip.
            setTimeout(function(){
                if (peer.destroyed || peer.open) return;
                try { peer.reconnect(); } catch(_){}
            }, 1000);
        });
    }
    function _attachVisibilityRecovery(peerRef){
        if (typeof document === 'undefined' || !document.addEventListener) return function(){};
        var handler = function(){
            if (document.hidden) return;
            var p = peerRef && peerRef.current;
            if (!p) return;
            if (p.disconnected && !p.destroyed){
                try { p.reconnect(); } catch(_){}
            }
        };
        document.addEventListener('visibilitychange', handler);
        return function(){ document.removeEventListener('visibilitychange', handler); };
    }

    /**
     * Host a room.
     *
     * v1.0 contract preserved: with default opts (`maxGuests=1`,
     * `acceptMedia=false`), behaves exactly as before — first guest
     * triggers `onGuestConnect`, second guest gets `roomFullPayload` + close.
     *
     * v1.1 additions:
     *   maxGuests       — cap concurrent guests (default 1; pass Infinity for broadcast tools)
     *   onMaxReached    — invoked when an over-cap guest tries to connect
     *                     (default: send roomFullPayload + close the connection)
     *   acceptMedia     — listen for peer.on('call') incoming media calls
     *   onIncomingCall  — (call, conn?) — caller decides answer(stream) and onstream wiring
     *   enableVisibilityRecovery — auto-reconnect peer signaling after tab unblur
     */
    function hostRoom(opts){
        opts = opts || {};
        var prefix = opts.prefix || '';
        var code = opts.code;
        var roomFullPayload = opts.roomFullPayload || ROOM_FULL_PAYLOAD;
        var maxGuests = opts.maxGuests != null ? opts.maxGuests : 1;
        var retriedUnavailable = false;
        var connections = new Set(); // open data connections only (closed ones removed via 'close' event)
        var peerRef = { current: null };

        function _isSettled(){
            // For 1v1 (maxGuests=1) we say "settled" once one guest is in.
            // For host-N we never settle — every new guest is welcome up to max.
            return maxGuests === 1 && connections.size >= 1;
        }

        function _onGuestArrived(conn){
            connections.add(conn);
            conn.on('close', function(){ connections.delete(conn); });
            // Defer the callback until the data channel is OPEN. Otherwise
            // any conn.send(...) inside the callback silently no-ops because
            // PeerJS fires `connection` on the host as soon as the SDP
            // handshake starts — before the data channel is established.
            // (Caught by real two-tab P2P test 2026-05-08; stubbed tests
            // missed this because they synthesise pre-opened connections.)
            //
            // CONTRACT FOR CALLERS: inside onGuestConnect the conn is ALREADY
            // open — never wrap setup in another conn.on('open', ...): that
            // event has fired and will not fire again, so the wrapped code is
            // dead. If a shared helper can receive both pre-open and open
            // conns, guard with `if (conn.open) fn(); else conn.on('open', fn)`.
            // (Bug class hit baby-monitor lobby, video-surveillance viewer
            // registration and volleyball match-start; fixed 2026-07-02.)
            if (conn.open){
                try { opts.onGuestConnect && opts.onGuestConnect(conn); } catch(_){}
            } else {
                conn.on('open', function(){
                    try { opts.onGuestConnect && opts.onGuestConnect(conn); } catch(_){}
                });
            }
        }

        function _rejectGuest(conn){
            // Default ROOM_FULL behaviour. Caller can override via onMaxReached
            // (e.g. tools that don't speak the [6] payload convention).
            if (typeof opts.onMaxReached === 'function'){
                try { opts.onMaxReached(conn); } catch(_){}
                return;
            }
            // Send THEN close — but not in the same tick. `close()` tears the
            // data channel down immediately, and a payload still sitting in
            // `bufferedAmount` goes with it: the refused guest is left on
            // "Подключено!" with no game and no reason, which is worse than
            // being told no. Measured 2026-08-26 on a real two-browser room —
            // the third player received NOTHING while prod telemetry shows the
            // toast arriving for others, i.e. the drop is a race, not a rule.
            // So: wait for the buffer to drain, with a cap so a wedged channel
            // cannot keep the handle alive forever.
            try {
                conn.on('open', function(){
                    try { conn.send(roomFullPayload); } catch(_){}
                    _closeWhenDrained(conn);
                });
            } catch(_){}
        }

        function _closeWhenDrained(conn, waitedMs){
            waitedMs = waitedMs || 0;
            var buffered = 0;
            try { buffered = (conn.dataChannel && conn.dataChannel.bufferedAmount) || 0; } catch(_){ buffered = 0; }
            if (buffered === 0 || waitedMs >= REJECT_DRAIN_MAX_MS){
                // One more turn of the event loop after the buffer reports
                // empty: "handed to the transport" is not yet "on the wire".
                setTimeout(function(){ try { conn.close(); } catch(_){} }, REJECT_DRAIN_TICK_MS);
                return;
            }
            setTimeout(function(){
                _closeWhenDrained(conn, waitedMs + REJECT_DRAIN_TICK_MS);
            }, REJECT_DRAIN_TICK_MS);
        }

        function _wirePeer(p){
            peerRef.current = p;
            p.on('open', function(){
                try { opts.onPeerOpen && opts.onPeerOpen(p.id); } catch(_){}
            });
            p.on('connection', function(conn){
                // Reconnect handling: if the incoming guest carries a
                // sessionToken (set by joinRoom v1.5+) and we already have
                // a connection with the same token, this is the same guest
                // reconnecting after F5/network blip — evict the stale
                // connection and admit the fresh one. Without this, a tab
                // reload during a 1v1 game looked like a third player and
                // got bounced with ROOM_FULL (30 events / 30 d on /checkers).
                var incomingToken = conn && conn.metadata && conn.metadata.sessionToken;
                if (incomingToken){
                    var stale = null;
                    connections.forEach(function(c){
                        if (c && c.metadata && c.metadata.sessionToken === incomingToken) stale = c;
                    });
                    if (stale){
                        connections.delete(stale);
                        try { stale.close(); } catch(_){}
                        // Standard arrival path so onGuestConnect fires with
                        // the fresh data channel — caller treats it as a
                        // resume and continues the game.
                        emit('guest_resumed', { tool: opts.tool, code_len: (code || '').length });
                        _onGuestArrived(conn);
                        return;
                    }
                }
                if (_isSettled() || connections.size >= maxGuests){
                    _rejectGuest(conn);
                    return;
                }
                _onGuestArrived(conn);
            });
            if (opts.acceptMedia){
                p.on('call', function(call){
                    try { opts.onIncomingCall && opts.onIncomingCall(call); } catch(_){}
                });
            }
            if (opts.enableVisibilityRecovery){
                _wireDisconnectedRecovery(p);
            }
            p.on('error', function(err){
                var t = err && err.type;
                if (t === 'unavailable-id' && !retriedUnavailable && typeof opts.regenCode === 'function'){
                    retriedUnavailable = true;
                    try { p.destroy(); } catch(_){}
                    code = opts.regenCode();
                    _wirePeer(_newPeer(prefix + code));
                    return;
                }
                emit('peer_error', { side: 'host', msg: String(t || err) });
                try { opts.onError && opts.onError(err); } catch(_){}
            });
        }

        if (typeof w.Peer === "undefined"){
            try { opts.onError && opts.onError({ type: 'peerjs-missing' }); } catch(_){}
            return { code: code, peer: null, connections: connections, broadcast: function(){}, destroy: function(){} };
        }

        _wirePeer(_newPeer(prefix + code));

        var cleanupVis = opts.enableVisibilityRecovery ? _attachVisibilityRecovery(peerRef) : function(){};

        return {
            get code(){ return code; },
            get peer(){ return peerRef.current; },
            connections: connections,
            // Fan-out to every connected guest. Useful for host-N tools.
            broadcast: function(payload){
                connections.forEach(function(c){
                    if (c && c.open){ try { c.send(payload); } catch(_){} }
                });
            },
            destroy: function(){
                cleanupVis();
                _safeDestroy(peerRef.current);
                peerRef.current = null;
                connections.clear();
            }
        };
    }

    /**
     * Join a room as guest.
     *
     * v1.0 contract preserved: calls exactly one of
     *   onConnected(conn) | onRoomNotFound(reason) | onError(err)
     * before going quiet.
     *
     * v1.1 additions:
     *   initiateMediaCall — () => MediaStream | Promise<MediaStream>
     *                       After onConnected, also peer.call() the host with this stream.
     *   onMediaStream     — (stream) callback when host's remote stream arrives.
     *   enableVisibilityRecovery — auto-reconnect peer signaling after tab unblur.
     *
     * v1.5 addition:
     *   mediaCallOptions  — passed straight through to peer.call() as its third
     *                       argument. The host side has always been able to set
     *                       call options (it owns the answer), while the guest
     *                       side hard-coded a two-argument call — so a guest
     *                       could not reach `sdpTransform`, and any codec
     *                       parameter it needed was silently unreachable.
     *                       /phone-mic needs it: the phone is the guest, and the
     *                       Opus fmtp line is the difference between a 32 kbit/s
     *                       mono stream and 510 kbit/s stereo (measured — see
     *                       public/js/phone-mic/audio-profile.js).
     */
    function joinRoom(opts){
        opts = opts || {};
        var prefix = opts.prefix || '';
        var code = opts.code;
        var timeoutMs = opts.timeoutMs || DEFAULT_TIMEOUT;
        var settled = false;
        var peerRef = { current: null };
        var timeoutId = null;
        var cleanupVis = function(){};

        function settle(fn, arg){
            if (settled) return;
            settled = true;
            if (timeoutId){ clearTimeout(timeoutId); timeoutId = null; }
            try { fn && fn(arg); } catch(_){}
        }

        if (typeof w.Peer === "undefined"){
            settle(opts.onError, { type: 'peerjs-missing' });
            return { peer: null, destroy: function(){} };
        }

        peerRef.current = _newPeer(null);
        if (opts.enableVisibilityRecovery){
            _wireDisconnectedRecovery(peerRef.current);
        }
        // Two very different failures used to share this one timer under the
        // single label broker_timeout: (a) the broker WebSocket never opened —
        // signaling unreachable; (b) the broker opened fine but the P2P data
        // channel to the host never did (ICE dead-end: STUN-only pairs behind
        // symmetric NAT / VPN / relay egress). Confirmed live 2026-07-20:
        // viewer peers registered on the broker, lived exactly 15s, died
        // unsettled — while the host was registered the whole time. Split the
        // reason so blades can show honest copy and the rj:* counters size
        // how often a relay would actually be needed.
        var peerOpened = false;
        timeoutId = setTimeout(function(){
            var p2p = peerOpened;
            settle(opts.onRoomNotFound, p2p ? 'p2p_timeout' : 'timeout');
            emit('peer_error', { side: 'guest', msg: p2p ? 'join_timeout_p2p' : 'join_timeout_broker' });
            emitJoinFailed(opts.tool, p2p ? 'p2p_timeout' : 'broker_timeout');
            _safeDestroy(peerRef.current);
            peerRef.current = null;
        }, timeoutMs);

        peerRef.current.on('open', function(){
            peerOpened = true;
            if (settled) return;
            var conn;
            try {
                // Stamp a sessionStorage-scoped token in metadata so the host
                // can recognise THIS browser tab after F5 / network blip and
                // evict its own stale connection instead of falsely firing
                // ROOM_FULL. Falls back to null on storage-blocked browsers
                // (private mode); host then treats every join as fresh.
                var token = _sessionToken(prefix, code);
                var metadata = token ? { sessionToken: token } : undefined;
                conn = peerRef.current.connect(prefix + code, {
                    reliable: true,
                    metadata: metadata
                });
            } catch(e){
                settle(opts.onError, e);
                return;
            }
            conn.on('open', function(){
                // ── The refusal is caught HERE, before the tool sees anything ──
                //
                // A host that has no seat left answers with ROOM_FULL_PAYLOAD and
                // closes. Tools were left to notice that themselves, and two ways
                // of not noticing were measured on 2026-08-26:
                //
                //   · the LISTENER race — both ends fire `open` in the same
                //     instant and the tool binds conn.on('data') inside its own
                //     onConnected, so a fast host emits into an EventEmitter with
                //     no subscriber. On /checkers the refusal reached the browser
                //     3 times out of 8;
                //   · the WRONG PROTOCOL — /chess speaks objects (`if (!msg.op)
                //     return`) and the payload is the array [6], so it is dropped
                //     outright. 272 sessions joined a room there in 30 days and a
                //     refused one was told «соперник вышел».
                //
                // Subscribing before onConnected fixes both for every caller: our
                // listener is registered first, so the race cannot be lost, and
                // the tool is handed a decoded reason instead of a raw frame.
                if (typeof opts.onRefused === 'function'){
                    conn.on('data', function(d){
                        if (_isRoomFull(d)){
                            emit('room_full_hit', { tool: opts.tool });
                            try { opts.onRefused('room_full'); } catch(_){}
                        }
                    });
                }
                settle(opts.onConnected, conn);

                // Optional: also dial the host with media after the data channel opens.
                if (typeof opts.initiateMediaCall === 'function'){
                    Promise.resolve()
                        .then(function(){ return opts.initiateMediaCall(); })
                        .then(function(stream){
                            if (!stream || !peerRef.current) return;
                            var call = opts.mediaCallOptions
                                ? peerRef.current.call(prefix + code, stream, opts.mediaCallOptions)
                                : peerRef.current.call(prefix + code, stream);
                            if (call && typeof opts.onMediaStream === 'function'){
                                call.on('stream', opts.onMediaStream);
                            }
                            // v1.4: hand the guest its outgoing MediaConnection so
                            // callers can replaceTrack() it (e.g. screen sharing).
                            if (call && typeof opts.onMediaCall === 'function'){
                                try { opts.onMediaCall(call); } catch(_){}
                            }
                        })
                        .catch(function(err){
                            // Media-call failure is informational, not fatal — data channel still works.
                            emit('peer_error', { side: 'guest', msg: 'media_call_failed: ' + (err && err.message || err) });
                        });
                }
            });
            conn.on('error', function(err){
                // An error on a channel that already opened is a lost link, not a failed join.
                if (!settled) emitJoinFailed(opts.tool, 'data_conn_error');
                settle(opts.onError, err);
            });
        });

        // For tools that opt into media on the GUEST side without dialing themselves
        // (host calls them — e.g. live-audio listener receives speaker's broadcast).
        if (opts.acceptMedia){
            peerRef.current.on('call', function(call){
                try { opts.onIncomingCall && opts.onIncomingCall(call); } catch(_){}
            });
        }

        peerRef.current.on('error', function(err){
            var t = err && err.type;
            // A join that already happened cannot fail. Field, 15.09.2026
            // (/phone-mic): every broker_unreachable beacon from phones came from
            // a phone that HAD joined and was working — its broker socket had
            // been open for minutes (nginx). It fired when the person left the
            // page or put the phone away, and rooms:join-failures read that as
            // "phones cannot reach the server". The loss is still peer_error.
            var decided = settled;
            if (t === 'peer-unavailable' || t === 'unavailable-id'){
                emit('peer_error', { side: 'guest', msg: t });
                if (!decided) emitJoinFailed(opts.tool, 'peer_unavailable');
                settle(opts.onRoomNotFound, t);
            } else {
                emit('peer_error', { side: 'guest', msg: String(t || err) });
                // Classify into the closed-enum dashboard reasons.
                var reason = 'internal';
                if (t === 'network' || t === 'socket-error' || t === 'server-error'){
                    reason = 'broker_unreachable';
                } else if (t === 'webrtc' || t === 'disconnected'){
                    reason = 'ice_failed';
                }
                if (!decided) emitJoinFailed(opts.tool, reason);
                settle(opts.onError, err);
            }
        });

        cleanupVis = opts.enableVisibilityRecovery ? _attachVisibilityRecovery(peerRef) : function(){};

        return {
            get peer(){ return peerRef.current; },
            destroy: function(){
                cleanupVis();
                if (timeoutId){ clearTimeout(timeoutId); timeoutId = null; }
                _safeDestroy(peerRef.current);
                peerRef.current = null;
            }
        };
    }

    /**
     * Walkie-talkie's "rotating host" pattern: tries to claim a room code as
     * host, falls back to guest mode if someone else holds it. Handles the
     * race documented in walkie-talkie.blade.php line 1417 ("someone else
     * claimed it simultaneously") by serialising attempts.
     *
     *   onClaimedHost(peer)   — we got the code first
     *   onMustJoin(reason)    — code is taken; caller should call joinRoom
     *   onError(err)          — non-claim error (peerjs missing, network)
     */
    function attemptHostClaim(opts){
        opts = opts || {};
        var prefix = opts.prefix || '';
        var code = opts.code;
        var settled = false;

        function settle(fn, arg){
            if (settled) return;
            settled = true;
            try { fn && fn(arg); } catch(_){}
        }

        if (typeof w.Peer === "undefined"){
            settle(opts.onError, { type: 'peerjs-missing' });
            return;
        }

        var peer = _newPeer(prefix + code);
        peer.on('open', function(){
            settle(opts.onClaimedHost, peer);
        });
        peer.on('error', function(err){
            var t = err && err.type;
            if (t === 'unavailable-id'){
                try { peer.destroy(); } catch(_){}
                settle(opts.onMustJoin, t);
            } else {
                try { peer.destroy(); } catch(_){}
                settle(opts.onError, err);
            }
        });
    }

    /**
     * ── Presence: guests are PEOPLE, not connections ───────────────────────
     *
     * Root-caused on /screen-share 2026-08-20 (report #1447: a couple watching a
     * film together suddenly saw "5 people" join their private room).
     *
     * Tools used to count live guests by pushing every incoming MediaConnection
     * into an array and removing it on close/error. Both assumptions break on a
     * real mobile network, and the failure is INVISIBLE to the host:
     *
     *   1. NO LIVENESS. A viewer whose phone loses signal closes nothing. The
     *      vendored PeerJS reacts only to ICE failed/closed; a vanished peer
     *      parks in "disconnected", which PeerJS merely LOGS. Measured on prod:
     *      a SIGKILLed viewer was still counted 131 s later — i.e. forever.
     *   2. NO IDENTITY. The same person reconnecting gets a fresh anonymous peer
     *      id and is counted AGAIN. Measured: one real viewer -> "2 зрителя".
     *      Five reconnects -> "5 зрителей"; at the cap the room answers FULL and
     *      locks the real guest out (viewers_peak spiked at exactly MAX in 15
     *      sessions / 30 d, alongside 64 room_full_hit events).
     *
     * So presence becomes a first-class concept instead of an array length:
     *   identity = the sessionStorage-scoped token joinRoom already stamps into
     *              conn.metadata (the same token that killed the /checkers
     *              ROOM_FULL false-positive) — a reconnect REPLACES its own slot;
     *   liveness = an app-level heartbeat over the data channel (ground truth,
     *              works even when ICE lies) + ICE state as the fast path;
     *   control  = kick / lock, so the host can act on what the roster shows.
     *
     * Back-compat during rollout: a guest running cached older JS never sends a
     * heartbeat. Such an entry is NEVER dropped by the heartbeat rule (only by
     * ICE), so a mixed-version fleet cannot evict live viewers — the heartbeat
     * rule arms itself per-entry, on the first heartbeat actually received.
     */
    /**
     * watchMedia — one reader for what is ACTUALLY going over the wire.
     *
     * Policy-free on purpose. It polls getStats on the peer connections a tool
     * hands it and normalises the answer; the tool decides what to do with it.
     * A shared READER is safe to share — a shared policy is not
     * ([[reference_shared_primitive_tuned_for_another_need]]).
     *
     * Why it exists: four room tools each open-coded a partial getStats poll
     * (video-call: rtt + loss; baby-monitor: rtt; consult booking: loss;
     * whiteboard: relay detection), and /screen-share — the one tool whose
     * entire product IS the video — had none at all. So when a host writes
     * «ВСЕ ЛАГАЕТ» there is nothing to look at: no frame rate, no bitrate, no
     * freeze count, on either side, ever. A class with no instrument cannot be
     * counted, let alone fixed.
     *
     *   var w = __RoomConnect.watchMedia({
     *       pcs: function(){ return [pc1, pc2]; },   // re-read every tick
     *       everyMs: 2000,
     *       onSample: function(s){ ... }             // normalised reading
     *   });
     *   w.stop();
     *
     * The reading (fields are null when the browser does not report them):
     *   send: { n, fps, w, h, kbps, srcFps, srcW, srcH, qlr, encMs, availKbps, per }
     *         — WORST across senders for fps/size (the viewer having the worst
     *           time is the one who complains), SUM for kbps.
     *         — `per`: one entry per sending connection, for a tool that steers
     *           each viewer on its own link instead of all of them by the worst
     *           one: { pc, dtMs, encFrames, srcFrames, qp, codec, kbps,
     *           targetKbps, availKbps, w, h, fps, srcFps, qlr, relayed, encMs,
     *           hwEnc, aKbps }.
     *           `qp` is the mean quantiser over the tick (null when the encoder
     *           does not report one); `relayed` is true when either end of the
     *           selected path is a TURN relay; `hwEnc` — whether the encoder is
     *           a hardware one (null: the browser did not say).
     *   recv: { fps, w, h, kbps, freezes, freezeMs, lostPct, jitterMs,
     *           codec, decMs, dropPct, bufMs, aKbps, aConcPct, aStereo }
     *         — `decMs`: milliseconds the decoder spent per frame over the tick;
     *           `dropPct`: share of received frames it threw away; `bufMs`: how
     *           long a frame waited in the playout buffer. All three are null
     *           until the browser has reported two readings.
     *   rttMs
     *
     * `send.srcFps` is the load-bearing one. A screen only produces frames when
     * it CHANGES, so a static text screen legitimately sources 1–2 fps and
     * sending 1–2 is perfect. Judging by absolute fps would call that broken.
     * fps / srcFps is the invariant that means the same thing for a spreadsheet
     * and for a film.
     */
    function watchMedia(opts){
        opts = opts || {};
        var everyMs = opts.everyMs || 2000;
        var stopped = false;
        var last = null;
        var prev = new Map();   // pc -> { bytesSent, bytesRecv, framesEnc, encTime, ts }

        function _num(v){ return (typeof v === 'number' && isFinite(v)) ? v : null; }
        function _add(sum, v){ var n = _num(v); return n === null ? sum : (sum === null ? n : sum + n); }

        function _readOne(pc){
            return pc.getStats().then(function(stats){
                var out = { o: null, i: null, src: null, pair: null, codec: null, icodec: null, relayed: false, ao: null, ai: null, acodec: null,
                            aoBytes: null, aiBytes: null, aiConc: null, aiTot: null };
                stats.forEach(function(r){
                    if (r.type === 'outbound-rtp' && r.kind === 'video') out.o = r;
                    else if (r.type === 'inbound-rtp' && r.kind === 'video') out.i = r;
                    // A connection can carry two sound tracks (/screen-share: the screen's sound and the
                    // host's voice, apart since 2026-10-08). Their counters add up — the last one read
                    // used to stand for the connection's sound — and the codec line is the busier one's.
                    else if (r.type === 'outbound-rtp' && r.kind === 'audio'){
                        out.aoBytes = _add(out.aoBytes, r.bytesSent);
                        if (!out.ao || (_num(r.bytesSent) || 0) > (_num(out.ao.bytesSent) || 0)) out.ao = r;
                    }
                    else if (r.type === 'inbound-rtp' && r.kind === 'audio'){
                        out.aiBytes = _add(out.aiBytes, r.bytesReceived);
                        // A dropout the listener HEARS: concealment that is silent is a source that stopped
                        // (the host turned the microphone off: its track sends nothing, and the receiver
                        // counts every sample of it as concealed — 100 % on the stand), not a loss.
                        var silent = _num(r.silentConcealedSamples) || 0;
                        var conc = _num(r.concealedSamples), tot = _num(r.totalSamplesReceived);
                        out.aiConc = _add(out.aiConc, conc === null ? null : conc - silent);
                        out.aiTot = _add(out.aiTot, tot === null ? null : tot - silent);
                        if (!out.ai || (_num(r.bytesReceived) || 0) > (_num(out.ai.bytesReceived) || 0)) out.ai = r;
                    }
                    else if (r.type === 'media-source' && r.kind === 'video') out.src = r;
                    else if (r.type === 'candidate-pair' && (r.state === 'succeeded' || r.nominated)) {
                        if (!out.pair || (r.availableOutgoingBitrate || 0) > (out.pair.availableOutgoingBitrate || 0)) out.pair = r;
                    }
                });
                if (typeof stats.get === 'function'){
                    if (out.o && out.o.codecId){
                        var c = stats.get(out.o.codecId);
                        if (c && c.mimeType) out.codec = c.mimeType;
                    }
                    if (out.i && out.i.codecId){
                        var ic = stats.get(out.i.codecId);
                        if (ic && ic.mimeType) out.icodec = ic.mimeType;
                    }
                    if (out.pair){
                        var lc = stats.get(out.pair.localCandidateId), rc = stats.get(out.pair.remoteCandidateId);
                        out.relayed = !!((lc && lc.candidateType === 'relay') || (rc && rc.candidateType === 'relay'));
                    }
                    // The sound's codec line: `channels: 2` is reported for mono Opus too, so
                    // «stereo» is the fmtp asking for it AND two channels (webrtc-audio-profile.js).
                    var aRef = out.ai || out.ao;
                    if (aRef && aRef.codecId){
                        var ac = stats.get(aRef.codecId);
                        if (ac && ac.mimeType) out.acodec = { channels: ac.channels || 0, fmtp: ac.sdpFmtpLine || '' };
                    }
                }
                return out;
            }).catch(function(){ return null; });
        }

        function tick(){
            if (stopped) return;
            var pcs = [];
            try { pcs = (typeof opts.pcs === 'function' ? opts.pcs() : opts.pcs) || []; } catch(_){ pcs = []; }
            pcs = pcs.filter(function(p){ return p && typeof p.getStats === 'function'; });
            if (!pcs.length) { last = null; return; }
            Promise.all(pcs.map(_readOne)).then(function(reads){
                if (stopped) return;
                var now = Date.now();
                var fresh = new Map();
                var send = null, recv = null, rtt = null;
                var sKbps = 0, rKbps = 0, seen = 0;

                reads.forEach(function(rd, idx){
                    if (!rd) return;
                    var pc = pcs[idx];
                    var p = prev.get(pc) || {};
                    var dt = p.ts ? (now - p.ts) / 1000 : 0;

                    if (rd.pair){
                        var t = _num(rd.pair.currentRoundTripTime);
                        if (t !== null) rtt = rtt === null ? t * 1000 : Math.max(rtt, t * 1000);
                    }
                    if (rd.o){
                        seen++;
                        send = send || { n: 0, fps: null, w: null, h: null, kbps: 0, srcFps: null, srcW: null, srcH: null, qlr: 'none', encMs: null, availKbps: null, per: [] };
                        send.n++;
                        var f = _num(rd.o.framesPerSecond);
                        if (f !== null) send.fps = send.fps === null ? f : Math.min(send.fps, f);
                        var wpx = _num(rd.o.frameWidth), hpx = _num(rd.o.frameHeight);
                        if (wpx) send.w = send.w === null ? wpx : Math.min(send.w, wpx);
                        if (hpx) send.h = send.h === null ? hpx : Math.min(send.h, hpx);
                        if (rd.o.qualityLimitationReason && rd.o.qualityLimitationReason !== 'none') send.qlr = rd.o.qualityLimitationReason;
                        var oneKbps = (dt > 0 && p.bytesSent != null) ? ((rd.o.bytesSent - p.bytesSent) * 8) / dt / 1000 : null;
                        if (oneKbps !== null) sKbps += oneKbps;
                        var oneEncMs = null;
                        if (dt > 0 && p.framesEnc != null && rd.o.framesEncoded > p.framesEnc && rd.o.totalEncodeTime != null){
                            var ms = ((rd.o.totalEncodeTime - p.encTime) * 1000) / (rd.o.framesEncoded - p.framesEnc);
                            oneEncMs = ms;
                            send.encMs = send.encMs === null ? ms : Math.max(send.encMs, ms);
                        }
                        var avail = null;
                        if (rd.pair){
                            var a = _num(rd.pair.availableOutgoingBitrate);
                            if (a !== null){ avail = Math.round(a / 1000); send.availKbps = Math.max(send.availKbps || 0, avail); }
                        }
                        // One viewer, one link: everything a tool needs to steer THIS
                        // connection. Counters are deltas over the tick; the first tick
                        // of a connection has no previous reading and is skipped.
                        var srcFrames = rd.src ? _num(rd.src.frames) : null;
                        var qpSum = _num(rd.o.qpSum);
                        var aSent = rd.aoBytes;
                        var oneAKbps = (dt > 0 && aSent !== null && p.aSent != null && aSent >= p.aSent) ? ((aSent - p.aSent) * 8) / dt / 1000 : null;
                        if (dt > 0 && p.framesEnc != null){
                            var encN = Math.max(0, (rd.o.framesEncoded || 0) - p.framesEnc);
                            send.per.push({
                                pc: pc, dtMs: dt * 1000, encFrames: encN,
                                // A source that was swapped restarts its counter: a negative step is "unknown", not "minus frames".
                                srcFrames: (srcFrames !== null && p.srcFrames != null && srcFrames >= p.srcFrames) ? srcFrames - p.srcFrames : null,
                                qp: (qpSum !== null && p.qpSum != null && encN > 0 && qpSum >= p.qpSum) ? (qpSum - p.qpSum) / encN : null,
                                codec: rd.codec, kbps: oneKbps === null ? 0 : oneKbps,
                                targetKbps: _num(rd.o.targetBitrate) === null ? null : rd.o.targetBitrate / 1000,
                                availKbps: avail, w: wpx, h: hpx, fps: f,
                                srcFps: rd.src ? _num(rd.src.framesPerSecond) : null,
                                srcW: rd.src ? _num(rd.src.width) : null, srcH: rd.src ? _num(rd.src.height) : null,
                                qlr: rd.o.qualityLimitationReason || 'none', relayed: !!rd.relayed,
                                encMs: oneEncMs,
                                // true / false, or null where the browser does not say (it tells only a page that is capturing)
                                hwEnc: rd.o.powerEfficientEncoder === true ? true : (rd.o.powerEfficientEncoder === false ? false : null),
                                // the sound sent on this connection (null: no sound sender, or the first tick)
                                aKbps: oneAKbps
                            });
                        }
                        p = Object.assign({}, p, { ts: now, bytesSent: rd.o.bytesSent, framesEnc: rd.o.framesEncoded, encTime: rd.o.totalEncodeTime, qpSum: qpSum, srcFrames: srcFrames, aSent: aSent });
                    }
                    if (rd.i){
                        recv = recv || { fps: null, w: null, h: null, kbps: 0, freezes: null, freezeMs: null, lostPct: null, jitterMs: null, codec: null, decMs: null, dropPct: null, bufMs: null };
                        recv.fps = _num(rd.i.framesPerSecond);
                        recv.w = _num(rd.i.frameWidth); recv.h = _num(rd.i.frameHeight);
                        recv.freezes = _num(rd.i.freezeCount);
                        var fd = _num(rd.i.totalFreezesDuration);
                        recv.freezeMs = fd === null ? null : Math.round(fd * 1000);
                        var lost = _num(rd.i.packetsLost) || 0, got = _num(rd.i.packetsReceived) || 0;
                        recv.lostPct = (lost + got) > 0 ? +(lost / (lost + got) * 100).toFixed(2) : null;
                        var j = _num(rd.i.jitter);
                        recv.jitterMs = j === null ? null : Math.round(j * 1000);
                        if (dt > 0 && p.bytesRecv != null) rKbps += ((rd.i.bytesReceived - p.bytesRecv) * 8) / dt / 1000;
                        // What is being decoded, and what it costs this device. Deltas over
                        // the tick: the totals are counters of the whole connection.
                        recv.codec = rd.icodec;
                        var fDec = _num(rd.i.framesDecoded), tDec = _num(rd.i.totalDecodeTime), fDrop = _num(rd.i.framesDropped);
                        var jbD = _num(rd.i.jitterBufferDelay), jbN = _num(rd.i.jitterBufferEmittedCount);
                        if (dt > 0 && fDec !== null && p.framesDec != null && fDec >= p.framesDec){
                            var nDec = fDec - p.framesDec;
                            if (nDec > 0 && tDec !== null && p.decTime != null) recv.decMs = ((tDec - p.decTime) * 1000) / nDec;
                            if (fDrop !== null && p.framesDrop != null && fDrop >= p.framesDrop){
                                var nDrop = fDrop - p.framesDrop;
                                recv.dropPct = (nDec + nDrop) > 0 ? (nDrop / (nDec + nDrop)) * 100 : null;
                            }
                        }
                        if (jbD !== null && jbN !== null && p.jbN != null && jbN > p.jbN) recv.bufMs = ((jbD - p.jbD) * 1000) / (jbN - p.jbN);
                        p = Object.assign({}, p, { ts: now, bytesRecv: rd.i.bytesReceived, framesDec: fDec, decTime: tDec, framesDrop: fDrop, jbD: jbD, jbN: jbN });
                    }
                    if (rd.ai){
                        // The sound that arrived: its bitrate, the share of it the decoder had to
                        // make up (concealed = a dropout the listener hears), and whether the wire
                        // is really stereo.
                        recv = recv || { fps: null, w: null, h: null, kbps: 0, freezes: null, freezeMs: null, lostPct: null, jitterMs: null, codec: null, decMs: null, dropPct: null, bufMs: null };
                        var aB = rd.aiBytes, aC = rd.aiConc, aT = rd.aiTot;
                        if (dt > 0 && aB !== null && p.aRecv != null && aB >= p.aRecv) recv.aKbps = ((aB - p.aRecv) * 8) / dt / 1000;
                        if (aC !== null && aT !== null && p.aTot != null && aT > p.aTot && aC >= p.aConc) recv.aConcPct = ((aC - p.aConc) / (aT - p.aTot)) * 100;
                        if (rd.acodec) recv.aStereo = /(^|;)\s*stereo=1/.test(rd.acodec.fmtp) && rd.acodec.channels === 2;
                        p = Object.assign({}, p, { ts: now, aRecv: aB, aConc: aC, aTot: aT });
                    }
                    if (rd.src && send){
                        send.srcFps = Math.max(send.srcFps || 0, _num(rd.src.framesPerSecond) || 0);
                        send.srcW = _num(rd.src.width); send.srcH = _num(rd.src.height);
                    }
                    if (p.ts) fresh.set(pc, p);
                });
                // Keyed by the connection object, so a closed call would sit in
                // the map for the life of the page. Rebuild from the live set.
                prev = fresh;

                if (send) send.kbps = Math.round(sKbps);
                if (recv) recv.kbps = Math.round(rKbps);
                last = { send: send, recv: recv, rttMs: rtt === null ? null : Math.round(rtt), at: now, seen: seen };
                try { opts.onSample && opts.onSample(last); } catch(_){}
            }).catch(function(){});
        }

        var timer = setInterval(tick, everyMs);
        tick();
        return {
            stop: function(){ stopped = true; clearInterval(timer); prev.clear(); },
            last: function(){ return last; }
        };
    }

    var HB_TYPE = '__hb';

    function attachHeartbeat(conn, everyMs){
        var t = setInterval(function(){
            try { if (conn && conn.open) conn.send({ type: HB_TYPE }); } catch(_){}
        }, everyMs || 5000);
        try { if (conn && conn.open) conn.send({ type: HB_TYPE }); } catch(_){}
        return function stop(){ clearInterval(t); };
    }

    /**
     * watchHost — the GUEST half of presence, and the half that was missing.
     *
     * Presence gave the host identity + liveness for its guests (report #1447).
     * Nothing gave the guest either one for the host, so a presenter whose
     * browser died left every viewer staring at a frozen frame under a live
     * badge. Measured 2026-08-30 on /screen-share with the host process
     * SIGKILLed: the picture froze at once, the tool's UI never changed for
     * 152 s and counting, and — the part that matters for anyone copying this —
     * `iceConnectionState` stalled at **disconnected** and NEVER reached
     * 'failed', while `connectionState` reached 'failed' at 16 s. A watchdog
     * written against iceConnectionState alone cannot see this at all. Prod
     * confirms it: `ice_failed` is 0 across 90 days on a tool where a third of
     * viewers lose the stream.
     *
     * Two independent signals, whichever speaks first:
     *   · the host's heartbeat over the data channel (ground truth, survives a
     *     media renegotiation, works when ICE lies);
     *   · the media peer connection failing (fast, ~16 s, no protocol needed).
     *
     * Back-compat, same trick as the host side: the heartbeat rule ARMS itself
     * on the first host heartbeat actually received, so a host running cached
     * older JS (which never sends one) can never be declared dead by it.
     *
     *   var w = __RoomConnect.watchHost({
     *       conn: dataConn, graceMs: 20000,
     *       onLost: function(why){ ... }, onBack: function(){ ... } });
     *   w.wireCall(call);   // optional media fast path
     *   w.stop();
     */
    function watchHost(opts){
        opts = opts || {};
        var graceMs = opts.graceMs || 20000;
        var sweepMs = opts.sweepMs || 4000;
        var conn = opts.conn || null;
        var armed = false;
        var lastSeen = Date.now();
        var lost = false;
        var stopped = false;

        function _lose(why){
            if (lost || stopped) return;
            lost = true;
            try { opts.onLost && opts.onLost(why); } catch(_){}
        }
        function _back(){
            lastSeen = Date.now();
            if (!lost || stopped) return;
            lost = false;
            try { opts.onBack && opts.onBack(); } catch(_){}
        }

        if (conn){
            conn.on('data', function(raw){
                var msg = raw;
                if (typeof raw === 'string'){ try { msg = JSON.parse(raw); } catch(_){ msg = null; } }
                if (msg && msg.type === HB_TYPE){ armed = true; _back(); return; }
                // Any traffic at all is proof of life, but only a heartbeat may
                // ARM the rule — otherwise a chatty-then-quiet host looks dead.
                lastSeen = Date.now();
            });
        }

        var timer = setInterval(function(){
            if (stopped || !armed) return;
            if (Date.now() - lastSeen > graceMs) _lose('heartbeat_lost');
        }, sweepMs);

        function wireCall(call){
            var pc = call && call.peerConnection;
            if (!pc) return;
            // BOTH properties: see the note above — Chromium answers on
            // connectionState here and leaves iceConnectionState behind.
            var check = function(){
                var c = pc.connectionState, i = pc.iceConnectionState;
                if (c === 'failed' || c === 'closed' || i === 'failed' || i === 'closed'){
                    _lose('media_' + (c === 'failed' || c === 'closed' ? c : i));
                } else if (c === 'connected' || i === 'connected' || i === 'completed'){
                    _back();
                }
            };
            pc.addEventListener('connectionstatechange', check);
            pc.addEventListener('iceconnectionstatechange', check);
            check();
        }

        return {
            wireCall: wireCall,
            /** Proof of life from outside (a fresh media stream, a app message). */
            touch: function(){ _back(); },
            isLost: function(){ return lost; },
            stop: function(){ stopped = true; clearInterval(timer); }
        };
    }

    function createPresence(opts){
        opts = opts || {};
        var max        = opts.max != null ? opts.max : Infinity;
        var hbGraceMs  = opts.hbGraceMs  || 20000;
        var iceGraceMs = opts.iceGraceMs || 12000;
        var sweepMs    = opts.sweepMs    || 4000;

        var entries = new Map();   // key -> entry
        var pending = new Map();   // key -> entry, waiting for the host to admit
        var approved = new Set();  // keys the host let in during this session
        var banned  = new Set();   // keys the host kicked
        var approval = !!opts.approval;
        var locked  = false;
        var destroyed = false;
        var seq = 0;

        function _changed(){
            if (destroyed) return;
            try { opts.onChange && opts.onChange(list()); } catch(_){}
        }
        function _keyOf(conn){
            var token = conn && conn.metadata && conn.metadata.sessionToken;
            return token || (conn && conn.peer) || null;
        }
        function _entryByPeer(peerId){
            var found = null;
            entries.forEach(function(e){ if (e.peerId === peerId) found = e; });
            return found;
        }
        function _drop(entry, reason){
            if (!entry || !entries.has(entry.key)) return;
            entries.delete(entry.key);
            if (entry.iceTimer){ clearTimeout(entry.iceTimer); entry.iceTimer = null; }
            if (entry.stopBeat){ try { entry.stopBeat(); } catch(_){} entry.stopBeat = null; }
            try { if (entry.call) entry.call.close(); } catch(_){}
            try { if (entry.conn) entry.conn.close(); } catch(_){}
            try { opts.onDrop && opts.onDrop(entry, reason || 'gone'); } catch(_){}
            _changed();
        }
        function _touch(entry){ if (entry) entry.lastSeen = Date.now(); }

        /* Approval mode — the door for a room whose ADDRESS is permanent.
           An ephemeral code is its own secret: it exists for one session and
           whoever has it was handed it minutes ago. A permanent address is the
           opposite — everyone who was ever given it can walk back in later, and
           that is the entire cost of the feature. So the host decides, once per
           person per session, the same way Whereby / Meet / Zoom personal rooms
           do. `lock` is not a substitute: it is reactive, it shuts the door only
           after the host has already noticed someone standing inside.
           Opt-in, and the tool turns it on for the permanent room only. */
        function _pendKey(conn){ return _keyOf(conn); }

        /* The host beats back — OPT-IN (`hostBeat: true`).
           Presence used to be one-directional: guests proved they were alive,
           the host never did, and a guest had no way to tell a quiet presenter
           from a dead one (measured on /screen-share: a SIGKILLed host left the
           viewer on a frozen frame under a live badge indefinitely).
           Why opt-in rather than on for everyone: this puts a new `{type:'__hb'}`
           object on the HOST→GUEST channel, and twenty-odd tools read that
           channel with handlers written before it existed — /file-transfer
           streams binary chunks over it. A tool turns this on together with its
           own test, the same way the guest→host heartbeat was rolled out
           per-entry rather than all at once. */
        function _startHostBeat(entry, conn){
            if (!opts.hostBeat) return;
            if (entry.stopBeat) entry.stopBeat();
            entry.stopBeat = attachHeartbeat(conn, opts.hostBeatMs || 5000);
        }

        function _wireConn(entry, conn){
            conn.on('data', function(raw){
                var msg = raw;
                if (typeof raw === 'string'){ try { msg = JSON.parse(raw); } catch(_){ msg = null; } }
                _touch(entry);
                // Arm the heartbeat rule only once this guest proves it speaks it.
                if (msg && msg.type === HB_TYPE) entry.hbArmed = true;
            });
            var gone = function(){
                // Ignore the close of a connection we already superseded via a
                // reconnect — otherwise admitting the fresh conn would instantly
                // drop the entry it had just refreshed.
                if (entry.conn !== conn) return;
                _drop(entry, 'conn_closed');
            };
            conn.on('close', gone);
            conn.on('error', gone);
        }

        function _wireCall(entry, call){
            var gone = function(){ if (entry.call === call) _drop(entry, 'call_closed'); };
            call.on('close', gone);
            call.on('error', gone);
            var pc = call.peerConnection;
            if (!pc) return;
            /* connectionState, not just iceConnectionState: measured on a
               SIGKILLed peer, Chromium leaves iceConnectionState at
               'disconnected' forever and reports the death on connectionState
               ('failed' at ~16 s). Reading only the ICE property is why the
               grace timer, not the failure itself, was doing the work here. */
            pc.onconnectionstatechange = function(){
                if (entry.call !== call) return;
                var cs = pc.connectionState;
                if (cs === 'failed' || cs === 'closed') _drop(entry, 'conn_' + cs);
            };
            pc.oniceconnectionstatechange = function(){
                if (entry.call !== call) return;
                var st = pc.iceConnectionState;
                if (st === 'failed' || st === 'closed'){ _drop(entry, 'ice_' + st); return; }
                if (st === 'disconnected'){
                    // PeerJS only logs this state, so a vanished phone would sit
                    // here forever. Give it a grace window to return, then cut.
                    if (entry.iceTimer) return;
                    entry.iceTimer = setTimeout(function(){
                        entry.iceTimer = null;
                        if (entry.call !== call) return;
                        var now = pc.iceConnectionState;
                        if (now === 'disconnected' || now === 'failed' || now === 'closed'){
                            _drop(entry, 'ice_disconnected');
                        }
                    }, iceGraceMs);
                    return;
                }
                if (st === 'connected' || st === 'completed'){
                    if (entry.iceTimer){ clearTimeout(entry.iceTimer); entry.iceTimer = null; }
                    _touch(entry);
                }
            };
        }

        /** Register/refresh a guest DATA connection. Verdict: ok|resumed|full|locked|banned|pending */
        function attachConn(conn){
            if (destroyed || !conn) return 'gone';
            var key = _keyOf(conn);
            if (!key) return 'gone';
            if (banned.has(key)) return 'banned';
            var entry = entries.get(key);
            // A knock, not a refusal: the data connection stays OPEN so the tool
            // can tell them they are waiting and admit them without a reconnect.
            // A guest already admitted this session (approved) walks straight
            // back in — a reload must not put them at the back of the queue.
            if (approval && !entry && !approved.has(key) && !locked){
                var pend = pending.get(key);
                if (pend){ pend.conn = conn; pend.peerId = conn.peer; _touch(pend); }
                else {
                    pending.set(key, { key: key, peerId: conn.peer, conn: conn,
                                       joinedAt: Date.now(), lastSeen: Date.now(), n: ++seq });
                    _changed();
                }
                return 'pending';
            }
            if (entry){
                var old = entry.conn;
                entry.conn = conn;
                entry.peerId = conn.peer;
                _touch(entry);
                if (old && old !== conn){ try { old.close(); } catch(_){} }
                _wireConn(entry, conn);
                _startHostBeat(entry, conn);
                _changed();
                return 'resumed';
            }
            if (locked) return 'locked';
            if (entries.size >= max) return 'full';
            entry = {
                key: key, peerId: conn.peer, conn: conn, call: null,
                joinedAt: Date.now(), lastSeen: Date.now(),
                hbArmed: false, iceTimer: null, n: ++seq
            };
            entries.set(key, entry);
            _wireConn(entry, conn);
            _startHostBeat(entry, conn);
            _changed();
            return 'ok';
        }

        /**
         * Decide on an incoming media call. Call BEFORE answering; on ok/resumed
         * answer it and then call wireCall(call) so ICE monitoring sees a live pc.
         */
        function admit(call){
            if (destroyed || !call) return 'gone';
            // Still knocking → no media. Without this the door is decorative:
            // the guest is refused on the data channel and then simply dials.
            var waiting = null;
            pending.forEach(function(e){ if (e.peerId === call.peer) waiting = e; });
            if (waiting) return 'pending';
            var entry = _entryByPeer(call.peer);
            if (entry){
                if (banned.has(entry.key)) return 'banned';
                var old = entry.call;
                entry.call = call;
                _touch(entry);
                if (old && old !== call){ try { old.close(); } catch(_){} }
                _changed();
                return 'resumed';
            }
            // No data conn seen for this peer (older client, or a call-only tool):
            // fall back to peer-id identity so the guest is still tracked.
            if (banned.has(call.peer)) return 'banned';
            if (locked) return 'locked';
            if (entries.size >= max) return 'full';
            entry = {
                key: call.peer, peerId: call.peer, conn: null, call: call,
                joinedAt: Date.now(), lastSeen: Date.now(),
                hbArmed: false, iceTimer: null, n: ++seq
            };
            entries.set(entry.key, entry);
            _changed();
            return 'ok';
        }

        function wireCall(call){
            var entry = _entryByPeer(call && call.peer);
            if (entry) _wireCall(entry, call);
        }

        /** The host says yes: move the knock into the room proper. */
        function approve(key){
            var pend = pending.get(key);
            if (!pend) return false;
            pending.delete(key);
            approved.add(key);
            if (locked || entries.size >= max){
                try { if (pend.conn && pend.conn.open) pend.conn.send(ROOM_FULL_PAYLOAD); } catch(_){}
                _changed();
                return false;
            }
            entries.set(key, { key: key, peerId: pend.peerId, conn: pend.conn, call: null,
                               joinedAt: Date.now(), lastSeen: Date.now(),
                               hbArmed: false, iceTimer: null, n: pend.n });
            var e = entries.get(key);
            _wireConn(e, pend.conn);
            _startHostBeat(e, pend.conn);
            _changed();
            return true;
        }

        /** The host says no. Refusing is not banning — they may knock again. */
        function deny(key){
            var pend = pending.get(key);
            if (!pend) return false;
            pending.delete(key);
            try { if (pend.conn && pend.conn.open) pend.conn.send({ type: 'denied' }); } catch(_){}
            setTimeout(function(){ try { pend.conn && pend.conn.close(); } catch(_){} }, 150);
            _changed();
            return true;
        }

        function pendingList(){
            var out = [];
            pending.forEach(function(e){ out.push({ key: e.key, n: e.n, joinedAt: e.joinedAt }); });
            out.sort(function(a, b){ return a.n - b.n; });
            return out;
        }

        function kick(key){
            var entry = entries.get(key);
            if (!entry) return false;
            banned.add(key);
            pending.delete(key);
            approved.delete(key);
            try { if (entry.conn && entry.conn.open) entry.conn.send({ type: 'kicked' }); } catch(_){}
            // Let the notice flush before tearing the transport down.
            setTimeout(function(){ _drop(entry, 'kicked'); }, 150);
            return true;
        }

        function list(){
            var out = [];
            entries.forEach(function(e){
                out.push({ key: e.key, n: e.n, joinedAt: e.joinedAt, lastSeen: e.lastSeen });
            });
            out.sort(function(a, b){ return a.n - b.n; });
            return out;
        }

        var sweeper = setInterval(function(){
            if (destroyed) return;
            var now = Date.now();
            var dead = [];
            entries.forEach(function(e){
                if (e.hbArmed && (now - e.lastSeen) > hbGraceMs) dead.push(e);
            });
            for (var i = 0; i < dead.length; i++) _drop(dead[i], 'heartbeat_lost');
        }, sweepMs);

        return {
            attachConn: attachConn,
            admit: admit,
            wireCall: wireCall,
            kick: kick,
            approve: approve,
            deny: deny,
            pending: pendingList,
            list: list,
            connFor: function(peerId){ var e = _entryByPeer(peerId); return e ? e.conn : null; },
            /* By KEY, because that is what the roster and approve() speak.
               list() deliberately exposes no peerId, so a tool holding a roster
               row had no way back to the channel. */
            connForKey: function(key){ var e = entries.get(key); return e ? e.conn : null; },
            calls: function(){ var out = []; entries.forEach(function(e){ if (e.call) out.push(e.call); }); return out; },
            size: function(){ return entries.size; },
            has: function(key){ return entries.has(key); },
            lock: function(v){ locked = !!v; _changed(); },
            isLocked: function(){ return locked; },
            destroy: function(){
                destroyed = true;
                entries.forEach(function(e){ if (e.stopBeat){ try { e.stopBeat(); } catch(_){} } });
                clearInterval(sweeper);
                entries.forEach(function(e){ if (e.iceTimer) clearTimeout(e.iceTimer); });
                entries.clear();
            }
        };
    }

    w.__RoomConnect = {
        DEFAULT_TIMEOUT: DEFAULT_TIMEOUT,
        ROOM_FULL_PAYLOAD: ROOM_FULL_PAYLOAD,
        generateCode: generateCode,
        validateCode: validateCode,
        validateCodeDetailed: validateCodeDetailed,
        emit: emit,
        hostRoom: hostRoom,
        joinRoom: joinRoom,
        attemptHostClaim: attemptHostClaim,
        markHostedRoom: markHostedRoom,
        clearHostedRoom: clearHostedRoom,
        isMyHostedRoom: isMyHostedRoom,
        createPresence: createPresence,
        watchHost: watchHost,
        attachHeartbeat: attachHeartbeat,
        watchMedia: watchMedia,
        HOST_STAMP_TTL_MS: HOST_STAMP_TTL_MS
    };
})(window);
